import type { SeverityLevel } from '@lighthouse/design'
import { RULE_LABELS, SOURCE_SYSTEMS } from '../../types/dataComb'
import type {
  CombColumnHistory, CombFinding, CombRun, CombSeverity, CombSummary, FindingState, FindingsQuery,
} from '../../types/dataComb'
import type { PagedResponse } from '../../types/api'
import type { EtlJobCatalog } from '../../types/etlJobs'
import { isApiError, problemDetailText } from '../../services/apiError'
import { deriveRunGate } from '../../utils/adminGate'
import { ADMIN_ROLE_REQUIRED, errorKind, errorSummary } from '../../utils/format'
import { errorTextView } from '../../utils/haltDetail'
import type { ErrorTextView } from '../../utils/haltDetail'
import { NO_ENTRA_REMEDY, signInRequiredText } from '../jobs/haltClearFormat'

// Everything below the presentation maps is a pure decision the page renders, kept
// here so it is testable without a DOM (combFormat.test.ts). This module must stay
// importable under vitest's node environment: it imports types, the MSAL-free
// utils modules, types/dataComb's constants, services/apiError (which imports
// nothing) and the Scheduler's pure jobs/haltClearFormat — never services/api or
// src/auth, which read window at module load. Whether this build has Entra arrives
// as an argument.

/**
 * Presentation for the comb's three severities.
 *
 * The design system's severity scale has four steps (critical / high / medium /
 * low) and the comb has three. Rather than inventing a fourth comb level or
 * quietly relabelling a badge, each comb level borrows the tone that reads right
 * and keeps its own word: a `warning` finding renders on the `high` tone and
 * still says "Warning". Colour carries the urgency, the label carries the truth,
 * and the two never have to be reconciled.
 */
export const SEVERITY_TONE: Record<CombSeverity, SeverityLevel> = {
  critical: 'critical',
  warning: 'high',
  info: 'low',
}

export const SEVERITY_LABEL: Record<CombSeverity, string> = {
  critical: 'Critical',
  warning: 'Warning',
  info: 'Info',
}

/**
 * How a provenance reads in the UI.
 *
 * Null means the finding is not about row data — landing residue, or a table the
 * sweep could not measure — which is a different statement from
 * `SourceSystem.Unknown`, a real value that rows written through generic CRUD
 * actually carry.
 */
export function provenanceLabel(sourceSystem: string | null): string {
  if (sourceSystem === null) return 'n/a'
  if (sourceSystem === 'Unknown') return 'Unrecorded'
  return sourceSystem
}

/** Highest first — the order the header chips and the table sort in. */
export const SEVERITY_ORDER: CombSeverity[] = ['critical', 'warning', 'info']

/**
 * How long a finding has stood, in words.
 *
 * "Since this morning" and "for three weeks" call for different responses, and a
 * bare day count makes the reader do that conversion on every row.
 */
export function ageLabel(ageDays: number, isNew: boolean): string {
  if (isNew) return 'New'
  if (ageDays < 1) return 'Today'
  if (ageDays === 1) return '1 day'
  if (ageDays < 14) return `${ageDays} days`
  if (ageDays < 60) return `${Math.round(ageDays / 7)} weeks`
  return `${Math.round(ageDays / 30)} months`
}

/** "4.2s", "312ms" — a comb pass is measured in seconds, not the ETL's minutes. */
export function combDuration(durationMs: number | null): string {
  if (durationMs == null) return '—'
  if (durationMs < 1000) return `${durationMs}ms`
  const seconds = durationMs / 1000
  if (seconds < 90) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${Math.round(seconds % 60)}s`
}

/**
 * Run status to the design system's status scale, or null for a plain gray badge.
 *
 * `Running` is `warning`, not green: the summary only ever served completed
 * passes, but the Pass select lists every pass, and a pass with no completion
 * (still going, or its worker died) has checked nothing yet, so it must not wear
 * the colour of a clean result. For the same reason a status this build does not
 * know is null — neutral, as every other outcome map in the SPA renders an
 * unknown value — and never the green of a clean pass.
 */
export function runStatusTone(status: string): 'good' | 'warning' | 'critical' | null {
  switch (status) {
    case 'Succeeded':
      return 'good'
    case 'SucceededWithWarnings':
    case 'Running':
      return 'warning'
    case 'Failed':
    case 'Halted':
    case 'Cancelled':
      return 'critical'
    default:
      return null
  }
}

/**
 * Why a FAILED pass did not complete, as this caller may see it; null for any other pass, or a failure with nothing
 * recorded.
 *
 * The server withholds a pass's error from every non-administrator, whatever it says (D8, fail closed): the comb runs
 * during a halt, so its failure can name the database's files. Withheld, the page says DETAIL_ADMIN_ONLY — the
 * server's `lastErrorWithheld` decides, never the caller's role.
 */
export function passErrorView(run: CombRun): Exclude<ErrorTextView, { kind: 'none' }> | null {
  if (run.status !== 'Failed') return null
  const view = errorTextView(run.lastError, run.lastErrorWithheld)
  return view.kind === 'none' ? null : view
}

/**
 * The wording matters here.
 *
 * A pass that finds problems <em>succeeded</em> — findings are its product, not
 * its failure — so "Completed with findings" rather than "Completed with
 * warnings". Only a sweep that could not run is a failure.
 */
export function runStatusLabel(status: string): string {
  switch (status) {
    case 'Succeeded':
      return 'Clean'
    case 'SucceededWithWarnings':
      return 'Completed with findings'
    case 'Running':
      return 'Running'
    case 'Failed':
      return 'Failed'
    default:
      return status
  }
}

// ---------------------------------------------------------------------------
// Page states. Each answers "what may the page say" from what the server has
// actually returned, so that a request that failed, or has not come back yet,
// never renders as a clean warehouse or as a comb that never ran.
// ---------------------------------------------------------------------------

/**
 * Which state the page is in, from the summary query.
 *
 * `never-run` needs a LOADED summary whose `latestRun` is null. An undefined
 * summary is not that: it is a request in flight, between retries, paused
 * offline, or failed, and reading it as "has not run" tells an operator nothing
 * was checked when nobody asked.
 */
export type SummaryView = 'loading' | 'error' | 'never-run' | 'ready'

export function summaryView(summary: CombSummary | undefined, isError: boolean): SummaryView {
  if (summary) return summary.latestRun ? 'ready' : 'never-run'
  return isError ? 'error' : 'loading'
}

/**
 * A summary is held but the latest request for it failed. The page keeps showing
 * the held summary (summaryView), so it must SAY it is the one last loaded:
 * drawn as current, it is how a page shows yesterday's all-clear over a request
 * that did not come back — the Overview's Critical Attention, reading the same
 * query, already shows that state as an error.
 */
export function summaryRefreshFailed(summary: CombSummary | undefined, isError: boolean): boolean {
  return summary !== undefined && isError
}

/**
 * Which state the findings list is in.
 *
 * An error wins over any page still held: an empty page followed by a failed
 * refetch would otherwise keep saying "Nothing outstanding" over a request that
 * did not come back. `empty` needs a defined page with no rows, and only that
 * may reach the "nothing matched" copy.
 */
export type FindingsView = 'loading' | 'error' | 'empty' | 'rows'

export function findingsView(
  findings: PagedResponse<CombFinding> | undefined,
  isLoading: boolean,
  isError: boolean,
): FindingsView {
  if (isError) return 'error'
  if (isLoading || !findings) return 'loading'
  return findings.data.length === 0 ? 'empty' : 'rows'
}

/**
 * Which state a column's fill-rate history is in.
 *
 * `error` and `empty` are different statements ("the request failed" and "no
 * measurement is retained"), and an undefined history that is neither loading
 * nor failed (a paused query) is still loading, never "none retained".
 */
export type HistoryView = 'not-applicable' | 'error' | 'loading' | 'empty' | 'single' | 'series'

export function historyView(
  columnName: string | null,
  data: CombColumnHistory | undefined,
  isLoading: boolean,
  isError: boolean,
): HistoryView {
  if (!columnName) return 'not-applicable'
  if (isError && !data) return 'error'
  if (isLoading || !data) return 'loading'
  const passes = new Set(data.points.map((p) => p.runId)).size
  if (passes === 0) return 'empty'
  return passes === 1 ? 'single' : 'series'
}

/**
 * Which state a `?finding=` deep link's drawer is in.
 *
 * No finding and no error is a request still in flight (or paused), so it is
 * `loading` whatever the query's own flags say.
 */
export type FindingLinkView = 'finding' | 'loading' | 'not-found' | 'forbidden' | 'error'

export function findingLinkView(finding: CombFinding | null | undefined, error: unknown): FindingLinkView {
  if (finding) return 'finding'
  if (error) {
    const kind = errorKind(error)
    if (kind === 'not-found') return 'not-found'
    if (kind === 'forbidden') return 'forbidden'
    return 'error'
  }
  return 'loading'
}

/**
 * The deep-linked finding's retry rule: a 404 is an answer (past retention, or
 * never existed) and asking again changes neither, so it is not retried. Anything
 * else gets the app's default single retry.
 */
export function shouldRetryFindingLink(failureCount: number, error: unknown): boolean {
  return errorKind(error) !== 'not-found' && failureCount < 1
}

// ---------------------------------------------------------------------------
// Passes. The summary, and so every count on the page, describes the LATEST
// completed pass only. Older passes are reachable by run id, and the page must
// then say less rather than borrow the latest pass's numbers.
// ---------------------------------------------------------------------------

/** Run ids compare case-insensitively: the wire is lowercase, a pasted link may not be. */
export function sameRunId(a: string | null | undefined, b: string | null | undefined): boolean {
  return a != null && b != null && a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * Whether a string can be a run id at all: the `Guid?` the findings endpoint
 * binds. A malformed `?runId=` would come back as a 400, and "that is not a run
 * id" is the more useful answer. Accepts the hyphenated and 32-digit forms,
 * braces optional, as `Guid.TryParse` does.
 */
export function isRunIdShaped(value: string): boolean {
  return /^\{?[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}\}?$/i.test(value.trim())
}

/**
 * - `latest`: the pass the summary describes. Counts apply.
 * - `earlier`: another pass, found in a listed page of passes. Its header is known; its counts are not.
 * - `unlisted`: a well-formed id not among the passes listed. Findings are still asked for by id.
 * - `malformed`: not a run id. Nothing is asked for.
 */
export type PassKind = 'latest' | 'earlier' | 'unlisted' | 'malformed'

export interface ResolvedPass {
  kind: PassKind
  /**
   * The id the findings query is pinned to. Set for every well-formed pass, the
   * latest included, so the rows come from the pass the header names even if
   * another completes between the two requests.
   */
  queryRunId: string | null
  /** The pass's header, or null when this page has not listed it. */
  run: CombRun | null
  /**
   * The pass has no completion recorded. DataCombService.RunAsync saves the
   * findings, the profiles and the completion in one SaveChanges, so such a pass
   * has no findings to list, and its empty list must not be shown as a result.
   */
  isIncomplete: boolean
}

export function resolvePass(
  requested: string | null,
  summary: CombSummary,
  ...lists: (CombRun[] | undefined)[]
): ResolvedPass {
  const latest = summary.latestRun
  const wanted = requested?.trim() || null

  if (!wanted || sameRunId(wanted, latest?.runId)) {
    return { kind: 'latest', queryRunId: latest?.runId ?? null, run: latest, isIncomplete: false }
  }

  if (!isRunIdShaped(wanted)) {
    return { kind: 'malformed', queryRunId: null, run: null, isIncomplete: false }
  }

  const run = lists.flatMap((l) => l ?? []).find((r) => sameRunId(r.runId, wanted)) ?? null
  return {
    kind: run ? 'earlier' : 'unlisted',
    queryRunId: run?.runId ?? wanted,
    run,
    isIncomplete: run !== null && run.completedAt === null,
  }
}

/**
 * Whether the previous findings page may stand in while the next loads: only for
 * the same pass. One pass's rows under another pass's header would be findings
 * attributed to a pass that did not record them.
 */
export function sameFindingsPass(previousKey: readonly unknown[] | undefined, runId: string | undefined): boolean {
  const previous = previousKey?.[2] as FindingsQuery | undefined
  return previous !== undefined && previous.runId === runId
}

/** One entry in the Pass select. */
export function passLabel(run: CombRun, latestRunId: string | null): string {
  if (sameRunId(run.runId, latestRunId)) return `${run.runKey} · latest`
  if (run.completedAt === null) return `${run.runKey} · not completed`
  return `${run.runKey} · ${runStatusLabel(run.status)}`
}

/**
 * The Pass select's options: the listed passes, newest first, plus whichever of
 * the latest and the selected pass is not among them. A Select whose value is not
 * in its data renders blank, which would hide which pass the page is showing.
 */
export function passOptions(
  runs: CombRun[] | undefined,
  latest: CombRun | null,
  pass: ResolvedPass,
): { value: string; label: string }[] {
  const listed = [...(runs ?? [])]
  if (latest && !listed.some((r) => sameRunId(r.runId, latest.runId))) listed.unshift(latest)
  const selected = pass.run
  if (selected && !listed.some((r) => sameRunId(r.runId, selected.runId))) listed.push(selected)

  const options = listed.map((r) => ({ value: r.runId, label: passLabel(r, latest?.runId ?? null) }))
  if (pass.kind === 'unlisted' && pass.queryRunId) {
    options.push({ value: pass.queryRunId, label: `${pass.queryRunId} · not listed` })
  }
  return options
}

/** The Pass select's value for the pass on screen. */
export function passValue(pass: ResolvedPass): string | null {
  return pass.run?.runId ?? pass.queryRunId
}

/** Said where the counts would be, when a pass other than the latest is on screen. */
export const COUNTS_LATEST_ONLY = 'Counts are computed for the latest pass only'

export interface FilterOptions {
  severityChips: { severity: CombSeverity; label: string }[]
  rules: { value: string; label: string }[]
  sources: { value: string; label: string }[]
  states: { value: FindingState; label: string }[]
  /** Null when the counts are shown. */
  countsCaption: string | null
}

/**
 * Which counts to show for a pass.
 *
 * Every count the summary carries is computed server-side over the latest
 * completed pass. For any other pass the labels drop their numbers rather than
 * borrow those, and nothing is counted client-side from a page, which would only
 * ever count what happens to be on screen. The rule and provenance lists for an
 * earlier pass are the full vocabularies instead of the latest pass's subset, so
 * a rule that fired then but not now can still be filtered on.
 *
 * `selected` keeps an active filter visible: a Select whose value is missing
 * from its options renders blank while the filter still applies.
 */
export function filterOptions(
  summary: CombSummary,
  isLatest: boolean,
  selected: { ruleId: string | null; sourceSystem: string | null } = { ruleId: null, sourceSystem: null },
): FilterOptions {
  const ruleLabel = (id: string) => RULE_LABELS[id] ?? id

  const severityChips = SEVERITY_ORDER.map((severity) => {
    if (!isLatest) return { severity, label: SEVERITY_LABEL[severity] }
    // The server omits a severity with nothing outstanding, so an absent one is a measured zero.
    const count = summary.severities.find((x) => x.severity === severity)?.count ?? 0
    return { severity, label: `${SEVERITY_LABEL[severity]} ${count}` }
  })

  const rules = isLatest
    ? summary.rules.map((r) => ({ value: r.ruleId, label: `${ruleLabel(r.ruleId)} (${r.count})` }))
    : [...new Set([...Object.keys(RULE_LABELS), ...summary.rules.map((r) => r.ruleId)])]
        .map((id) => ({ value: id, label: ruleLabel(id) }))

  // Only the real provenances. The findings with none (landing residue, an
  // unmeasurable table, a lossy key) are reached by their rule instead, rather
  // than by inventing a wire value for "not applicable".
  const sources = isLatest
    ? summary.sources
        .filter((s) => s.sourceSystem !== null)
        .map((s) => ({ value: s.sourceSystem!, label: `${provenanceLabel(s.sourceSystem)} (${s.count})` }))
    : SOURCE_SYSTEMS.map((s) => ({ value: s, label: provenanceLabel(s) }))

  if (selected.ruleId && !rules.some((r) => r.value === selected.ruleId)) {
    rules.push({ value: selected.ruleId, label: ruleLabel(selected.ruleId) })
  }
  if (selected.sourceSystem && !sources.some((s) => s.value === selected.sourceSystem)) {
    sources.push({ value: selected.sourceSystem, label: provenanceLabel(selected.sourceSystem) })
  }

  const states: { value: FindingState; label: string }[] = isLatest
    ? [
        { value: 'outstanding', label: `Outstanding (${summary.outstanding})` },
        { value: 'acknowledged', label: `Acknowledged (${summary.acknowledged})` },
        { value: 'all', label: 'All' },
      ]
    : [
        { value: 'outstanding', label: 'Outstanding' },
        { value: 'acknowledged', label: 'Acknowledged' },
        { value: 'all', label: 'All' },
      ]

  return { severityChips, rules, sources, states, countsCaption: isLatest ? null : COUNTS_LATEST_ONLY }
}

/**
 * What an empty findings list may say.
 *
 * - `clean`: the only green. The latest pass, which completed, has nothing outstanding.
 * - `failed-pass`: the pass failed, so an empty list is what it did not measure, not a result.
 * - `unknown-pass`: this page could not describe the pass, so it cannot vouch for its list either.
 * - `earlier-none`: an earlier pass with nothing still outstanding. True, but not today's all-clear.
 * - `no-match`: a filter.
 */
export type EmptyVerdict =
  | { kind: 'clean'; acknowledged: number }
  | { kind: 'failed-pass' }
  | { kind: 'unknown-pass' }
  | { kind: 'earlier-none' }
  | { kind: 'no-match' }

export function emptyVerdict(
  state: FindingState,
  filtered: boolean,
  pass: ResolvedPass,
  summary: CombSummary,
): EmptyVerdict {
  if (pass.run?.status === 'Failed') return { kind: 'failed-pass' }
  if (pass.run === null) return { kind: 'unknown-pass' }
  if (pass.kind === 'latest') {
    return state === 'outstanding' && summary.outstanding === 0
      ? { kind: 'clean', acknowledged: summary.acknowledged }
      : { kind: 'no-match' }
  }
  return state === 'outstanding' && !filtered ? { kind: 'earlier-none' } : { kind: 'no-match' }
}

/**
 * Whether acknowledging a finding carries forward.
 *
 * The next pass copies the acknowledgement of the NEWEST record of a condition
 * (DataCombService.LoadFindingHistoryAsync), so acknowledging a row from an
 * earlier pass marks that record and need not keep the condition off the next
 * pass's list. `unknown` when the latest pass is not known.
 */
export function acknowledgementScope(
  findingRunId: string,
  latestRunId: string | null | undefined,
): 'latest' | 'earlier' | 'unknown' {
  if (!latestRunId) return 'unknown'
  return sameRunId(findingRunId, latestRunId) ? 'latest' : 'earlier'
}

// ---------------------------------------------------------------------------
// Acknowledging and restoring a finding (R3, 2026-10-01).
//
// Both are Lighthouse.Admin AND pinned to an Entra sign-in on the server
// (EtlWriteAuthorization), exactly as clearing a halt is, so the control is gated
// the way the Scheduler gates "Clear this halt…" (jobs/haltClearFormat.ts
// clearHaltGate): on the catalogue's server-evaluated `canAcknowledgeCombFindings`,
// failing closed — never on a token claim, never on `isLighthouseAdmin` (the READ
// answer, which says yes to the shared API key in Development) and no longer on the
// summary's `permission`, which the server still serves for an older bundle.
// ---------------------------------------------------------------------------

/** When the server refused without saying why. */
export const ACK_REFUSED_FALLBACK = 'The server says this session may not acknowledge or restore a finding.'

/** When the catalogue has not answered whether this session may acknowledge. */
export const ACK_UNAVAILABLE_TEXT =
  'Acknowledging a finding is not offered: the job catalogue has not said whether this session may. An '
  + 'administrator acknowledges one here or through the API (POST /api/v1/data-comb/findings/{id}/acknowledge; '
  + 'DELETE on the same path restores it), with an Entra Bearer sign-in holding Lighthouse.Admin.'

/** The sentence for a 403 the server gave no reason for: the policy's own refusal carries no body. */
export const ACK_ROLE_REQUIRED =
  'Acknowledging or restoring a finding requires the Lighthouse.Admin app role, which this account does not hold.'

export type AcknowledgeGate =
  | { kind: 'offer' }
  | { kind: 'refused'; reason: string; remedy: string | null }
  | { kind: 'unavailable' }

/**
 * Whether to offer Acknowledge and "Return to outstanding".
 *
 * - `offer` ONLY when the server says `canAcknowledgeCombFindings: true`.
 * - `refused` when it says false: its reason, plus "This build has no Entra sign-in." when that is why no
 *   session here could ever do it.
 * - `unavailable` when the catalogue, its permission, or the field is missing (loading, failed, an older API
 *   that does not pin the comb). A write fails closed: never offered on an answer nobody gave.
 */
export function acknowledgeGate(catalog: EtlJobCatalog | undefined, entraConfigured: boolean): AcknowledgeGate {
  const permission = catalog?.permission
  if (!permission) return { kind: 'unavailable' }
  if (permission.canAcknowledgeCombFindings === true) return { kind: 'offer' }
  if (permission.canAcknowledgeCombFindings === false) {
    return {
      kind: 'refused',
      reason: permission.deniedReason?.trim() || ACK_REFUSED_FALLBACK,
      remedy: entraConfigured ? null : NO_ENTRA_REMEDY,
    }
  }
  return { kind: 'unavailable' }
}

/**
 * Why an acknowledge or a restore failed, for the notification.
 *
 * - Not sent (no Entra token) / 401: sign in with Entra — or, in a build with none, that it cannot be done here.
 * - 403: the server's problem detail first (the identity refusal names it), else the missing role.
 * - 404: the server's own words, which name the comb's retention window.
 * - 5xx / network: unknown whether it was recorded; retrying is safe, because a repeat changes nothing.
 */
export function ackErrorText(error: unknown, entraConfigured: boolean, verb: 'acknowledge' | 'restore'): string {
  const signIn = signInRequiredText(error, entraConfigured)
  if (signIn) return signIn

  const detail = isApiError(error) ? problemDetailText(error.body) : null

  switch (errorKind(error)) {
    case 'forbidden':
      return detail ?? ACK_ROLE_REQUIRED
    case 'not-found':
      return detail ?? "No such finding: it may have passed the comb's retention window."
    case 'server':
    case 'network':
      return `Could not ${verb} the finding: unknown whether it was recorded — retrying is safe; a repeat changes `
        + 'nothing.'
    default:
      return errorSummary(error) ?? `Could not ${verb} the finding.`
  }
}

// ---------------------------------------------------------------------------
// Run a pass now.
// ---------------------------------------------------------------------------

/** The comb's job id in the catalogue (JobSchedulerService registers it as Diagnostics). */
export const DATA_COMB_JOB_ID = 'data-comb'

/** Where "Run a pass now" goes: the Scheduler, opened on the comb's job. */
export const RUN_NOW_PATH = `/jobs?job=${DATA_COMB_JOB_ID}`

export interface RunNowGate {
  disabled: boolean
  /** Shown on hover. Null when there is nothing to say. */
  tooltip: string | null
}

/**
 * Whether "Run a pass now" is offered, gated as the Scheduler gates Run.
 *
 * - No catalogue (loading, failed, an older API): offered. It only navigates, the
 *   Scheduler gates the real request itself, and JobsPage treats an absent
 *   permission the same way, so a missing answer never locks anyone out.
 * - The server says the caller may not trigger jobs: disabled, with its reason.
 *   That is `canTriggerJobs` (deriveRunGate), the Bearer-pinned write's answer —
 *   NOT the admin read answer, since in Development the shared API key is an
 *   administrator for the read pages and still cannot run anything.
 * - The catalogue has no data-comb job: disabled, because the Scheduler would have
 *   nothing to run.
 * - A halt is active: the trigger endpoint refuses every category the halt does
 *   not list in `exemptCategories`. Diagnostics is exempt, so the comb stays
 *   offered and the tooltip says it runs during a halt. An API that exempts
 *   nothing would refuse it, so then it is disabled.
 */
export function runNowGate(catalog: EtlJobCatalog | undefined): RunNowGate {
  if (!catalog) return { disabled: false, tooltip: null }

  const gate = deriveRunGate(catalog, false)
  if (gate.state === 'denied') return { disabled: true, tooltip: gate.reason || ADMIN_ROLE_REQUIRED }

  const job = catalog.jobs.find((j) => j.jobId === DATA_COMB_JOB_ID)
  if (!job) {
    return {
      disabled: true,
      tooltip:
        `The job catalogue Automaton published has no ${DATA_COMB_JOB_ID} job, so the Scheduler has nothing `
        + 'to run. The catalogue is republished each time the worker starts.',
    }
  }

  const halt = catalog.activeHalt
  if (halt) {
    const exempt = (halt.exemptCategories ?? []).includes(job.category)
    return exempt
      ? {
          disabled: false,
          tooltip:
            `ETL is halted (${halt.reason} on ${halt.runKey}), but the Data Comb runs during a halt: `
            + `${job.category} jobs are exempt from it.`,
        }
      : {
          disabled: true,
          tooltip:
            `ETL is halted (${halt.reason} on ${halt.runKey}) and this API exempts no ${job.category} job `
            + 'from a halt, so the request would be refused until the halt is cleared.',
        }
  }

  return { disabled: false, tooltip: null }
}
